'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';
import { Button } from '@vm/ui';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // Report the error to Sentry
    Sentry.captureException(error, {
      tags: {
        component: 'GlobalError',
        app: 'repetix',
        critical: 'true',
      },
      contexts: {
        error: {
          digest: error.digest,
          message: error.message,
          name: error.name,
        },
      },
      level: 'error',
    });
  }, [error]);

  return (
    <html>
      <body>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Application Error
            </h1>
            
            <p className="text-gray-600 mb-6">
              We're experiencing a technical issue. Our team has been automatically notified and is working to resolve it.
            </p>
            
            {error.digest && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
                <p className="text-sm text-gray-600 mb-1">Error Reference:</p>
                <code className="text-xs text-gray-800 bg-white px-2 py-1 rounded border">
                  {error.digest}
                </code>
              </div>
            )}
            
            <div className="space-y-3">
              <Button
                onClick={reset}
                className="w-full"
                variant="primary"
              >
                Try Again
              </Button>

              <Button
                onClick={() => window.location.href = '/'}
                variant="secondary"
                className="w-full"
              >
                Go to Home
              </Button>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                If the problem persists, contact{' '}
                <a
                  href="mailto:info@vanmoose.com"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  info@vanmoose.com
                </a>
              </p>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-left">
                <p className="text-sm text-red-800 font-medium mb-2">Development Error:</p>
                <pre className="text-xs text-red-700 whitespace-pre-wrap break-words">
                  {error.message}
                </pre>
                {error.stack && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-600 cursor-pointer">Stack Trace</summary>
                    <pre className="text-xs text-red-600 mt-1 whitespace-pre-wrap break-words">
                      {error.stack}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}